import {
    Directive,
    ElementRef,
    OnInit,
    OnDestroy,
    TemplateRef,
    ComponentRef,
    NgZone,
    Renderer2,
    inject,
    signal,
    computed,
    input,
    effect,
} from "@angular/core";

import { Overlay, OverlayRef, ConnectedPosition } from "@angular/cdk/overlay";

import { ComponentPortal } from "@angular/cdk/portal";
import { CerrixHoverCardComponent } from "./cerrix-hover-card.component";

@Directive({
    selector: "[cerrixHover]",
})
export class CerrixHoverCardDirective implements OnInit, OnDestroy {
    hover = input<TemplateRef<any> | null>(null, { alias: "cerrixHover" });
    hoverClass = input<string>("", { alias: "cerrixHoverClass" });
    delay = input<number>(200);
    align = input<"start" | "center" | "end">("start");
    placement = input<"above" | "below">("below");
    recheckEmptyOnShow = input<boolean>(false);

    isPointerOverHost = signal(false);
    isPointerOverOverlay = signal(false);

    closeToleranceMs = 300;

    shouldBeOpen = computed(() => this.isPointerOverHost() || this.isPointerOverOverlay());

    isTemplateEmpty = signal(true);

    private readonly host = inject(ElementRef<HTMLElement>);
    private readonly overlay = inject(Overlay);
    private readonly ngZone = inject(NgZone);
    private readonly renderer = inject(Renderer2);

    private overlayRef?: OverlayRef;
    private overlayComponentRef?: ComponentRef<CerrixHoverCardComponent>;

    private showId?: number;
    private hideId?: number;

    private unlistenHost?: () => void;

    constructor() {
        // Check template emptiness whenever template changes
        effect(() => {
            const template = this.hover();
            this.isTemplateEmpty.set(template ? this.checkTemplateEmpty(template) : true);
        });

        // Re-check template emptiness when opening (optional input)
        effect(() => {
            if (this.recheckEmptyOnShow() && this.shouldBeOpen() && this.hover()) {
                this.isTemplateEmpty.set(this.checkTemplateEmpty(this.hover()!));
            }
        });

        // Overlay state management
        effect(() => {
            this.clearTimers();

            const template = this.hover();
            if (!template || this.isTemplateEmpty()) {
                this.destroyOverlay();
                return;
            }

            const wantsOpen = this.shouldBeOpen();
            const hasOverlay = !!this.overlayRef;

            if (wantsOpen && !hasOverlay) {
                this.scheduleOpen();
            } else if (!wantsOpen && hasOverlay) {
                this.scheduleClose();
            }
        });
    }

    ngOnInit(): void {
        this.setupHostListeners();
    }

    ngOnDestroy(): void {
        this.clearTimers();
        this.destroyOverlay();
        this.unlistenHost?.();
    }

    private setupHostListeners() {
        this.unlistenHost = this.ngZone.runOutsideAngular(() => {
            const el = this.host.nativeElement;

            const enter = this.renderer.listen(el, "pointerenter", () =>
                this.isPointerOverHost.set(true),
            );

            const leave = this.renderer.listen(el, "pointerleave", () =>
                this.isPointerOverHost.set(false),
            );

            return () => {
                enter();
                leave();
            };
        });
    }

    private scheduleOpen() {
        const delayMs = this.delay();

        this.ngZone.runOutsideAngular(() => {
            this.showId = window.setTimeout(
                () => this.ngZone.run(() => this.createOverlay()),
                delayMs,
            );
        });
    }

    private scheduleClose() {
        const delayMs = this.closeToleranceMs;

        this.ngZone.runOutsideAngular(() => {
            this.hideId = window.setTimeout(
                () => this.ngZone.run(() => this.destroyOverlay()),
                delayMs,
            );
        });
    }

    private clearTimers() {
        if (this.showId) {
            clearTimeout(this.showId);
            this.showId = undefined;
        }
        if (this.hideId) {
            clearTimeout(this.hideId);
            this.hideId = undefined;
        }
    }

    private createOverlay() {
        if (this.overlayRef || !this.hover() || this.isTemplateEmpty()) return;

        const strategy = this.overlay
            .position()
            .flexibleConnectedTo(this.host)
            .withPositions(this.buildPositions(this.align(), this.placement()))
            .withFlexibleDimensions(false)
            .withPush(false);

        this.overlayRef = this.overlay.create({
            positionStrategy: strategy,
            scrollStrategy: this.overlay.scrollStrategies.close(),
            hasBackdrop: false,
        });

        const portal = new ComponentPortal(CerrixHoverCardComponent);
        this.overlayComponentRef = this.overlayRef.attach(portal);

        const component = this.overlayComponentRef.instance;
        component.template = this.hover()!;
        component.panelClass = this.hoverClass();
        this.overlayComponentRef.changeDetectorRef.detectChanges();

        this.bindOverlayPointerEvents(component);

        this.overlayRef.detachments().subscribe(() => this.resetOverlayRefs());
    }

    private destroyOverlay() {
        const ref = this.overlayRef;
        if (!ref) return;

        this.ngZone.runOutsideAngular(() => {
            ref.dispose();
            this.resetOverlayRefs();
        });
    }

    private resetOverlayRefs() {
        this.overlayRef = undefined;
        this.overlayComponentRef = undefined;
        this.isPointerOverOverlay.set(false);
    }

    private bindOverlayPointerEvents(component: CerrixHoverCardComponent) {
        component.pointerEnter.subscribe(() => this.isPointerOverOverlay.set(true));
        component.pointerLeave.subscribe(() => this.isPointerOverOverlay.set(false));
    }

    private buildPositions(
        align: "start" | "center" | "end",
        placement: "above" | "below",
    ): ConnectedPosition[] {
        const originX = align;
        const overlayX = align;

        const primary: ConnectedPosition =
            placement === "above"
                ? { originX, originY: "top", overlayX, overlayY: "bottom", offsetY: -8 }
                : { originX, originY: "bottom", overlayX, overlayY: "top", offsetY: 8 };

        // vertical fallback
        const verticalFlip: ConnectedPosition = {
            originX,
            overlayX,
            originY: primary.originY === "bottom" ? "top" : "bottom",
            overlayY: primary.overlayY === "top" ? "bottom" : "top",
            offsetY: (primary.offsetY ?? 0) * -1,
        };

        // horizontal fallback
        const horizontalFlip: ConnectedPosition = {
            originX: originX === "start" ? "end" : originX === "end" ? "start" : "center",
            overlayX: originX === "start" ? "end" : originX === "end" ? "start" : "center",
            originY: primary.originY,
            overlayY: primary.overlayY,
            offsetY: primary.offsetY,
        };

        // full flip (horizontal + vertical)
        const bothFlip: ConnectedPosition = {
            originX: horizontalFlip.originX,
            overlayX: horizontalFlip.overlayX,
            originY: verticalFlip.originY,
            overlayY: verticalFlip.overlayY,
            offsetY: verticalFlip.offsetY,
        };

        return [primary, verticalFlip, horizontalFlip, bothFlip];
    }

    private checkTemplateEmpty(template: TemplateRef<any>): boolean {
        const view = template.createEmbeddedView({});
        view.detectChanges();

        const ELEMENT_NODE = 1;
        const TEXT_NODE = 3;

        const hasContent = view.rootNodes.some(
            (n: any) =>
                n.nodeType === ELEMENT_NODE || (n.nodeType === TEXT_NODE && n.textContent?.trim()),
        );

        view.destroy();
        return !hasContent;
    }
}
